/**
 * Data Contract Validator
 * Validates data before sending to backend to catch issues early.
 * Based on backend/audit_data_contract.py logic.
 */

/**
 * Validate user registration data before sending to backend
 * @param {Object} userDataCamel - User data in camelCase format
 * @returns {{valid: boolean, errors: string[]}}
 */
export const validateUserRegistrationData = (userDataCamel) => {
  const errors = [];
  
  // Required fields for registration (location fields moved to profile)
  const requiredFields = ['email', 'name', 'phone', 'gender', 'birthYear'];
  for (const field of requiredFields) {
    const value = userDataCamel[field];
    if (value === undefined || value === null) {
      errors.push(`Missing required field: ${field}`);
    } else if (typeof value === 'string' && value.trim() === '') {
      errors.push(`Required field '${field}' is empty or whitespace`);
    } else if (field === 'birthYear' && typeof value !== 'number') {
      errors.push(`Required field 'birthYear' must be a number`);
    }
  }
  
  // Email format validation
  if (userDataCamel.email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userDataCamel.email)) {
      errors.push(`Invalid email format: ${userDataCamel.email}`);
    }
  }
  
  // Blood type validation (if provided)
  if (userDataCamel.bloodType !== null && userDataCamel.bloodType !== undefined) {
    const validBloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
    if (!validBloodTypes.includes(userDataCamel.bloodType)) {
      errors.push(`Invalid blood_type value '${userDataCamel.bloodType}'. Must be one of: ${validBloodTypes.join(', ')}`);
    }
  }
  
  // Gender validation (if provided)
  if (userDataCamel.gender) {
    const validGenders = ['male', 'female', 'other', 'prefer_not_to_say'];
    if (!validGenders.includes(userDataCamel.gender)) {
      errors.push(`Invalid gender value '${userDataCamel.gender}'. Must be one of: ${validGenders.join(', ')}`);
    }
  }
  
  // Birth year validation (if provided)
  if (userDataCamel.birthYear !== null && userDataCamel.birthYear !== undefined) {
    if (typeof userDataCamel.birthYear !== 'number') {
      errors.push(`Field 'birthYear' should be number, got ${typeof userDataCamel.birthYear}`);
    } else {
      const currentYear = new Date().getFullYear();
      const minYear = 1900;
      if (userDataCamel.birthYear < minYear || userDataCamel.birthYear > currentYear) {
        errors.push(`Field 'birthYear' has invalid value ${userDataCamel.birthYear}. Must be between ${minYear} and ${currentYear}`);
      }
    }
  }
  
  // Boolean fields validation
  const booleanFields = ['isBloodDonor', 'isOrganDonor', 'bodyDonation', 'sosOptIn'];
  for (const field of booleanFields) {
    if (userDataCamel[field] !== undefined && userDataCamel[field] !== null) {
      if (typeof userDataCamel[field] !== 'boolean') {
        errors.push(`Field '${field}' should be boolean, got ${typeof userDataCamel[field]}`);
      }
    }
  }
  
  // Organs pledged validation (if provided)
  if (userDataCamel.organsPledged !== undefined && userDataCamel.organsPledged !== null) {
    if (!Array.isArray(userDataCamel.organsPledged)) {
      errors.push(`Field 'organsPledged' should be array, got ${typeof userDataCamel.organsPledged}`);
    } else {
      const validOrgans = ['heart', 'liver', 'kidney', 'lung', 'pancreas', 'intestine', 'cornea', 'skin', 'bone', 'tendon'];
      userDataCamel.organsPledged.forEach((organ, index) => {
        if (typeof organ !== 'string') {
          errors.push(`Field 'organsPledged[${index}]' should be string, got ${typeof organ}`);
        } else if (!validOrgans.includes(organ)) {
          errors.push(`Field 'organsPledged[${index}]' has invalid value '${organ}'. Must be one of: ${validOrgans.join(', ')}`);
        }
      });
    }
  }
  
  // Donation type validation (if provided)
  if (userDataCamel.donationType !== null && userDataCamel.donationType !== undefined) {
    const validDonationTypes = ['living', 'deceased', 'both'];
    if (typeof userDataCamel.donationType !== 'string') {
      errors.push(`Field 'donationType' should be string, got ${typeof userDataCamel.donationType}`);
    } else if (!validDonationTypes.includes(userDataCamel.donationType)) {
      errors.push(`Field 'donationType' has invalid value '${userDataCamel.donationType}'. Must be one of: ${validDonationTypes.join(', ')}`);
      // Special check for common bug
      if (userDataCamel.donationType === 'organ' || userDataCamel.donationType === 'blood') {
        errors.push(`Field 'donationType' appears to be a tab type ('${userDataCamel.donationType}'), not a donation type. Should be 'living', 'deceased', or 'both'`);
      }
    }
  }
  
  // Emergency contact validation (if provided)
  if (userDataCamel.emergencyContact !== null && userDataCamel.emergencyContact !== undefined) {
    if (typeof userDataCamel.emergencyContact !== 'object' || Array.isArray(userDataCamel.emergencyContact)) {
      errors.push(`Field 'emergencyContact' should be object, got ${typeof userDataCamel.emergencyContact}`);
    } else {
      const ec = userDataCamel.emergencyContact;
      const requiredEcFields = ['name', 'phone', 'relationship'];
      for (const field of requiredEcFields) {
        if (!ec[field] || (typeof ec[field] === 'string' && ec[field].trim() === '')) {
          errors.push(`emergencyContact missing or empty required field: ${field}`);
        }
      }
    }
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

